import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Map as GoogleMap, AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { MapFullscreenControl } from '@/components/maps/MapFullscreenControl'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { doc, getDoc } from 'firebase/firestore'
import { openPropertyProfile } from '@/lib/property-navigation'
import { db } from '@/lib/firebase'
import { ChevronRight, X } from 'lucide-react'
import { useMapsAppCheckReady } from '@/components/maps/MapsAppCheckBridge'
import {
  MapLocationSearch,
  type MapLocationTarget,
} from '@/components/maps/MapLocationSearch'
import { MapTypeSync, MapTypeToggleButtons } from '@/components/maps/MapTypeToggle'
import { PropertySearchBox } from '@/components/search/PropertySearchBox'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { compareAddressesNatural, splitAddressDisplay } from '@/lib/address'
import { BIDDLE_BLUE } from '@/lib/brand'
import { searchPropertiesInArea } from '@/lib/callables'
import { geohashes6ForBounds, geohashesForBounds } from '@/lib/geohash'
import {
  buildPinIndex,
  MAP_CLUSTER_MIN_COUNT,
  MAP_PIN_HARD_BUDGET,
  MAP_SEARCH_MIN_ZOOM,
  MAP_SOFT_RENDER_BUDGET,
  prepareAreaSearchPins,
  queryVisibleClusters,
  type PinIndex,
  type VisibleCluster,
} from '@/lib/map-pins'
import { getSearchBackend } from '@/lib/search-backend'
import { cn } from '@/lib/utils'
import { parseProperty, type Property } from '@/types/models'

function propertyFromPin(pin: {
  id: string
  lat: number
  lng: number
  label: string
}): Property {
  return parseProperty(pin.id, {
    fullAddress: pin.label,
    // Area search deliberately omits hot status; profile fetch supplies it.
    hasActiveBiddles: false,
    location: { latitude: pin.lat, longitude: pin.lng },
    geohash_8: '',
  })
}

const DEFAULT_CENTER = { lat: -37.8136, lng: 144.9631 }
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
/** Advanced markers require a real Cloud Map ID — never fall back to DEMO_MAP_ID. */
const MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined)?.trim() || undefined

type MapViewport = {
  center: { lat: number; lng: number }
  bounds: { south: number; west: number; north: number; east: number }
  zoom: number
}

type PropertyGroup = {
  key: string
  lat: number
  lng: number
  properties: Property[]
}

/** Same grouping key precision as iOS MapView.groupPropertiesByLocation. */
function groupPropertiesByLocation(properties: Property[]): PropertyGroup[] {
  const groups: Record<string, PropertyGroup> = {}
  for (const property of properties) {
    if (property.location.latitude === 0 && property.location.longitude === 0) continue
    const key = `${property.location.latitude.toFixed(8)},${property.location.longitude.toFixed(8)}`
    const existing = groups[key]
    if (existing) {
      existing.properties.push(property)
    } else {
      groups[key] = {
        key,
        lat: property.location.latitude,
        lng: property.location.longitude,
        properties: [property],
      }
    }
  }
  return Object.values(groups)
}

function coordKey(lat: number, lng: number) {
  return `${lat.toFixed(8)},${lng.toFixed(8)}`
}

/** Reports when the map's Pegman Street View panorama is open. */
function StreetViewVisibilityWatcher({
  onVisibleChange,
}: {
  onVisibleChange: (visible: boolean) => void
}) {
  const map = useMap()

  useEffect(() => {
    if (!map) return
    const panorama = map.getStreetView()
    const sync = () => onVisibleChange(panorama.getVisible())
    sync()
    const listener = panorama.addListener('visible_changed', sync)
    return () => listener.remove()
  }, [map, onVisibleChange])

  return null
}

function MapController({
  active,
  onViewport,
  flyTo,
  onMapClick,
}: {
  active: boolean
  onViewport: (viewport: MapViewport) => void
  flyTo: MapLocationTarget | null
  onMapClick: () => void
}) {
  const map = useMap()
  const [searchParams] = useSearchParams()
  const propertyOpen = Boolean(searchParams.get('property'))
  const wasPropertyOpenRef = useRef(false)

  useEffect(() => {
    if (!map) return
    const publish = () => {
      const c = map.getCenter()
      const b = map.getBounds()
      const zoom = map.getZoom()
      if (!c || !b || zoom == null) return
      const ne = b.getNorthEast()
      const sw = b.getSouthWest()
      onViewport({
        center: { lat: c.lat(), lng: c.lng() },
        bounds: {
          south: sw.lat(),
          west: sw.lng(),
          north: ne.lat(),
          east: ne.lng(),
        },
        zoom,
      })
    }
    publish()
    const idleListener = map.addListener('idle', publish)
    const clickListener = map.addListener('click', onMapClick)
    return () => {
      idleListener.remove()
      clickListener.remove()
    }
  }, [map, onViewport, onMapClick])

  // After keep-alive hide/show, force a layout pass so tiles/camera stay correct.
  useEffect(() => {
    if (!map || !active) return
    const center = map.getCenter()
    const zoom = map.getZoom()
    const timer = window.setTimeout(() => {
      if (center) map.setCenter(center)
      if (zoom != null) map.setZoom(zoom)
    }, 40)
    return () => window.clearTimeout(timer)
  }, [map, active])

  // Property profile (Street View) can disturb map layout; nudge on close.
  useEffect(() => {
    if (!map) return
    if (wasPropertyOpenRef.current && !propertyOpen) {
      const center = map.getCenter()
      const zoom = map.getZoom()
      const timer = window.setTimeout(() => {
        google.maps.event.trigger(map, 'resize')
        if (center) map.setCenter(center)
        if (zoom != null) map.setZoom(zoom)
      }, 40)
      wasPropertyOpenRef.current = propertyOpen
      return () => window.clearTimeout(timer)
    }
    wasPropertyOpenRef.current = propertyOpen
  }, [map, propertyOpen])

  useEffect(() => {
    if (!map || !flyTo) return
    if (flyTo.bounds) {
      map.fitBounds(flyTo.bounds, 48)
      return
    }
    map.panTo({ lat: flyTo.lat, lng: flyTo.lng })
    // Only change zoom when the caller asks — pin taps must preserve zoom.
    if (flyTo.zoom != null) map.setZoom(flyTo.zoom)
  }, [map, flyTo])

  return null
}

/** Recomputes Supercluster leaves/clusters on map idle (debounced). */
function ClusterLayerController({
  index,
  enabled,
  onClusters,
}: {
  index: PinIndex | null
  enabled: boolean
  onClusters: (items: VisibleCluster[]) => void
}) {
  const map = useMap()
  const timerRef = useRef(0)

  useEffect(() => {
    if (!map || !enabled || !index) {
      onClusters([])
      return
    }

    const publish = () => {
      window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        const b = map.getBounds()
        const zoom = map.getZoom()
        if (!b || zoom == null) return
        const ne = b.getNorthEast()
        const sw = b.getSouthWest()
        let items = queryVisibleClusters(
          index,
          {
            south: sw.lat(),
            west: sw.lng(),
            north: ne.lat(),
            east: ne.lng(),
          },
          zoom,
        )
        // Soft render budget: prefer clusters over flooding the DOM.
        if (items.length > MAP_SOFT_RENDER_BUDGET) {
          const tighter = queryVisibleClusters(
            index,
            {
              south: sw.lat(),
              west: sw.lng(),
              north: ne.lat(),
              east: ne.lng(),
            },
            Math.max(0, Math.floor(zoom) - 1),
          )
          if (tighter.length < items.length) items = tighter
        }
        onClusters(items)
      }, 50)
    }

    publish()
    const idleListener = map.addListener('idle', publish)
    return () => {
      idleListener.remove()
      window.clearTimeout(timerRef.current)
    }
  }, [map, index, enabled, onClusters])

  return null
}

function ClusterExpandController({
  target,
}: {
  target: { clusterId: number; lat: number; lng: number; index: PinIndex } | null
}) {
  const map = useMap()
  useEffect(() => {
    if (!map || !target) return
    const expansionZoom = Math.min(
      target.index.getClusterExpansionZoom(target.clusterId),
      20,
    )
    map.panTo({ lat: target.lat, lng: target.lng })
    map.setZoom(expansionZoom)
  }, [map, target])
  return null
}

function PropertyGroupMarker({
  group,
  selected,
  onSelect,
}: {
  group: PropertyGroup
  selected: boolean
  onSelect: () => void
}) {
  const count = group.properties.length
  const multi = count > 1
  const hot = group.properties.some((p) => p.hasActiveBiddles)
  const label = multi
    ? `${count} properties at this location`
    : group.properties[0]?.fullAddress || 'Property'

  return (
    <AdvancedMarker
      position={{ lat: group.lat, lng: group.lng }}
      onClick={() => {
        onSelect()
      }}
      title={label}
      zIndex={selected ? 10 : multi ? 5 : 1}
    >
      <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        className={cn(
          'flex cursor-pointer items-center justify-center rounded-full font-bold text-white transition-transform',
          multi
            ? 'h-8 w-8 text-[13px] hover:scale-110'
            : selected
              ? 'h-5 w-5 scale-110'
              : 'h-3.5 w-3.5 hover:scale-125',
        )}
        style={{
          backgroundColor: BIDDLE_BLUE,
          boxShadow: selected
            ? '0 0 0 3px #fff, 0 2px 6px rgba(0,0,0,0.35)'
            : hot
              ? `0 0 0 2.5px #fff, 0 0 0 5px ${BIDDLE_BLUE}55, 0 1px 3px rgba(0,0,0,0.3)`
              : '0 0 0 2.5px #fff, 0 1px 3px rgba(0,0,0,0.3)',
          cursor: 'pointer',
        }}
      >
        {multi ? count : null}
      </button>
    </AdvancedMarker>
  )
}

function ClusterBadgeMarker({
  item,
  onExpand,
}: {
  item: Extract<VisibleCluster, { kind: 'cluster' }>
  onExpand: () => void
}) {
  return (
    <AdvancedMarker
      position={{ lat: item.lat, lng: item.lng }}
      onClick={onExpand}
      title={`${item.count} properties`}
      zIndex={6}
    >
      <button
        type="button"
        aria-label={`${item.count} properties — zoom in`}
        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[13px] font-bold text-white transition-transform hover:scale-110"
        style={{
          backgroundColor: BIDDLE_BLUE,
          boxShadow: item.hot
            ? `0 0 0 2.5px #fff, 0 0 0 5px ${BIDDLE_BLUE}55, 0 1px 3px rgba(0,0,0,0.3)`
            : '0 0 0 2.5px #fff, 0 1px 3px rgba(0,0,0,0.3)',
        }}
      >
        {item.count >= 1000 ? `${(item.count / 1000).toFixed(1)}k` : item.count}
      </button>
    </AdvancedMarker>
  )
}

function LeafPinMarker({
  item,
  selected,
  onSelect,
}: {
  item: Extract<VisibleCluster, { kind: 'leaf' }>
  selected: boolean
  onSelect: () => void
}) {
  const count = item.count
  const multi = count > 1

  return (
    <AdvancedMarker
      position={{ lat: item.lat, lng: item.lng }}
      onClick={onSelect}
      title={item.label}
      zIndex={selected ? 10 : multi ? 5 : 1}
    >
      <button
        type="button"
        aria-label={item.label}
        aria-pressed={selected}
        className={cn(
          'flex cursor-pointer items-center justify-center rounded-full font-bold text-white transition-transform',
          multi
            ? 'h-8 w-8 text-[13px] hover:scale-110'
            : selected
              ? 'h-5 w-5 scale-110'
              : 'h-3.5 w-3.5 hover:scale-125',
        )}
        style={{
          backgroundColor: BIDDLE_BLUE,
          boxShadow: selected
            ? '0 0 0 3px #fff, 0 2px 6px rgba(0,0,0,0.35)'
            : item.hasActiveBiddles
              ? `0 0 0 2.5px #fff, 0 0 0 5px ${BIDDLE_BLUE}55, 0 1px 3px rgba(0,0,0,0.3)`
              : '0 0 0 2.5px #fff, 0 1px 3px rgba(0,0,0,0.3)',
        }}
      >
        {multi ? count : null}
      </button>
    </AdvancedMarker>
  )
}

/** Multi-unit pin picker — bottom sheet on mobile, centered modal on desktop. */
function PropertyGroupSheet({
  properties,
  onClose,
  onSelect,
}: {
  properties: Property[]
  onClose: () => void
  onSelect: (property: Property) => void
}) {
  const [exiting, setExiting] = useState(false)
  const sorted = useMemo(
    () =>
      [...properties].sort((a, b) =>
        compareAddressesNatural(a.fullAddress, b.fullAddress),
      ),
    [properties],
  )

  const requestClose = useCallback(() => {
    if (exiting) return
    setExiting(true)
    window.setTimeout(() => onClose(), 260)
  }, [exiting, onClose])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [requestClose])

  return (
    <div className="pointer-events-auto absolute inset-0 z-30 flex flex-col justify-end sm:items-center sm:justify-center sm:p-6">
      <button
        type="button"
        aria-label="Dismiss"
        className={cn(
          'absolute inset-0 bg-navy/30 backdrop-blur-[2px]',
          exiting ? 'map-sheet-backdrop-exit' : 'map-sheet-backdrop-enter',
        )}
        onClick={requestClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-group-sheet-title"
        className={cn(
          'relative flex w-full max-h-[70%] flex-col border border-border/60 bg-white/95 backdrop-blur-xl',
          'rounded-t-[1.75rem] shadow-[0_-12px_40px_rgba(11,31,58,0.18)]',
          'sm:max-h-[min(70vh,560px)] sm:max-w-md sm:rounded-2xl sm:shadow-2xl',
          exiting ? 'property-profile-panel-exit' : 'property-profile-panel-enter',
        )}
      >
        <div className="mx-auto mt-2.5 h-1.5 w-11 shrink-0 rounded-full bg-border sm:hidden" />
        <div className="flex items-center justify-between px-5 pb-2 pt-3 sm:pt-5">
          <h2 id="map-group-sheet-title" className="text-base font-bold text-charcoal">
            Properties at this location
          </h2>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-muted transition hover:bg-surface hover:text-charcoal"
          >
            <X className="h-5 w-5" strokeWidth={2.25} />
          </button>
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-6">
          {sorted.map((property) => {
            const { street, locality } = splitAddressDisplay(property.fullAddress)
            return (
              <li key={property.id} className="border-t border-border">
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-surface"
                  onClick={() => onSelect(property)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-charcoal">
                      {street || 'Address unavailable'}
                    </p>
                    {locality ? <p className="truncate text-sm text-muted">{locality}</p> : null}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted" />
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function MapCanvas({ active }: { active: boolean }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const focusPropertyId = searchParams.get('property')
  const appCheckReady = useMapsAppCheckReady()
  const [properties, setProperties] = useState<Property[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [groupSheetKey, setGroupSheetKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resultCount, setResultCount] = useState<number | null>(null)
  const [totalAvailable, setTotalAvailable] = useState<number | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [mapZoom, setMapZoom] = useState(13)
  const [visibleClusters, setVisibleClusters] = useState<VisibleCluster[]>([])
  const [expandTarget, setExpandTarget] = useState<{
    clusterId: number
    lat: number
    lng: number
    index: PinIndex
  } | null>(null)
  const [flyTo, setFlyTo] = useState<MapLocationTarget | null>(null)
  const [mapTypeId, setMapTypeId] = useState<'roadmap' | 'satellite'>('roadmap')
  const [streetViewOpen, setStreetViewOpen] = useState(false)
  const viewportRef = useRef<MapViewport | null>(null)
  const lastSearchKeyRef = useRef('')
  const lastFocusedPropertyRef = useRef<string | null>(null)

  const onMapTypeIdChange = useCallback((id: 'roadmap' | 'satellite') => {
    setMapTypeId(id)
  }, [])

  const onStreetViewVisibleChange = useCallback((visible: boolean) => {
    setStreetViewOpen(visible)
  }, [])

  const useClustering = properties.length >= MAP_CLUSTER_MIN_COUNT

  const pinIndex = useMemo(
    () => (useClustering ? buildPinIndex(properties) : null),
    [properties, useClustering],
  )

  const groups = useMemo(
    () => (useClustering ? [] : groupPropertiesByLocation(properties)),
    [properties, useClustering],
  )

  const propertiesById = useMemo(() => {
    const byId = new Map<string, Property>()
    for (const p of properties) byId.set(p.id, p)
    return byId
  }, [properties])

  const groupsByCoord = useMemo(() => {
    const byCoord = new Map<string, Property[]>()
    for (const p of properties) {
      const key = coordKey(p.location.latitude, p.location.longitude)
      const list = byCoord.get(key)
      if (list) list.push(p)
      else byCoord.set(key, [p])
    }
    return byCoord
  }, [properties])

  const groupSheet = useMemo(() => {
    if (!groupSheetKey) return null
    if (!useClustering) {
      return groups.find((g) => g.key === groupSheetKey) ?? null
    }
    const at = groupsByCoord.get(groupSheetKey)
    if (!at || at.length === 0) return null
    return {
      key: groupSheetKey,
      lat: at[0].location.latitude,
      lng: at[0].location.longitude,
      properties: at,
    } satisfies PropertyGroup
  }, [groupSheetKey, groups, groupsByCoord, useClustering])

  const onViewport = useCallback((viewport: MapViewport) => {
    viewportRef.current = viewport
    setMapZoom(viewport.zoom)
  }, [])

  const onClusters = useCallback((items: VisibleCluster[]) => {
    setVisibleClusters(items)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedId(null)
    setGroupSheetKey(null)
  }, [])

  const onLocate = useCallback((target: MapLocationTarget) => {
    setProperties([])
    setSelectedId(null)
    setGroupSheetKey(null)
    setResultCount(null)
    setTotalAvailable(null)
    setTruncated(false)
    setVisibleClusters([])
    lastSearchKeyRef.current = ''
    setFlyTo(target)
  }, [])

  // Search-tab / deep-link: ensure pin is on the map. Pin taps already have the
  // marker — don't pan/zoom (preserves the user's current zoom level).
  useEffect(() => {
    if (!focusPropertyId) {
      lastFocusedPropertyRef.current = null
      return
    }
    if (lastFocusedPropertyRef.current === focusPropertyId) return
    lastFocusedPropertyRef.current = focusPropertyId

    let cancelled = false
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'properties', focusPropertyId))
        if (cancelled || !snap.exists()) return
        const property = parseProperty(snap.id, snap.data() as Record<string, unknown>)
        const { latitude: lat, longitude: lng } = property.location
        if (!lat && !lng) return

        setGroupSheetKey(null)
        setSelectedId(property.id)

        let alreadyOnMap = false
        setProperties((prev) => {
          alreadyOnMap = prev.some((p) => p.id === property.id)
          if (alreadyOnMap) return prev
          return [property, ...prev]
        })
        // Only move the camera for external selection (search / deep link).
        if (!alreadyOnMap) {
          setFlyTo({ lat, lng, zoom: 17 })
        }
      } catch {
        // Modal still opens from the query param; map just won't pan.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [focusPropertyId])

  const onGroupSelect = useCallback(
    (group: PropertyGroup) => {
      if (group.properties.length === 1) {
        setGroupSheetKey(null)
        setSelectedId(group.properties[0].id)
        openPropertyProfile(navigate, group.properties[0].id)
        return
      }
      setSelectedId(null)
      setGroupSheetKey(group.key)
    },
    [navigate],
  )

  const onLeafSelect = useCallback(
    (item: Extract<VisibleCluster, { kind: 'leaf' }>) => {
      const key = coordKey(item.lat, item.lng)
      const at =
        groupsByCoord.get(key) ??
        item.propertyIds
          .map((id) => propertiesById.get(id))
          .filter((p): p is Property => p != null)
      if (at.length > 1 || item.count > 1) {
        setSelectedId(null)
        setGroupSheetKey(key)
        return
      }
      const property = at[0] ?? propertiesById.get(item.propertyId)
      if (!property) return
      setGroupSheetKey(null)
      setSelectedId(property.id)
      openPropertyProfile(navigate, property.id)
    },
    [groupsByCoord, navigate, propertiesById],
  )

  const searchZoomOk = mapZoom >= MAP_SEARCH_MIN_ZOOM

  const searchThisArea = useCallback(async () => {
    const viewport = viewportRef.current
    if (!viewport || loading) return
    if (viewport.zoom < MAP_SEARCH_MIN_ZOOM) return

    const backend = await getSearchBackend()
    const geohashes =
      backend === 'v2'
        ? geohashes6ForBounds(viewport.bounds, viewport.center)
        : geohashesForBounds(viewport.bounds, viewport.center)
    const boundsKey = [
      viewport.bounds.south.toFixed(5),
      viewport.bounds.west.toFixed(5),
      viewport.bounds.north.toFixed(5),
      viewport.bounds.east.toFixed(5),
    ].join(',')
    const key = `${backend}:${geohashes.slice().sort().join(',')}:${boundsKey}`
    if (key === lastSearchKeyRef.current && properties.length > 0) {
      return
    }

    setLoading(true)
    setError(null)
    setSelectedId(null)
    setGroupSheetKey(null)
    try {
      if (backend === 'v2') {
        const data = await searchPropertiesInArea(geohashes, 'pins')
        const parsed = (data.pins ?? []).map(propertyFromPin)
        // Geohash cells may over-fetch; only render pins inside the searched viewport.
        const prepared = prepareAreaSearchPins(
          parsed,
          viewport.bounds,
          viewport.center,
          MAP_PIN_HARD_BUDGET,
        )
        lastSearchKeyRef.current = key
        setProperties(prepared.kept)
        setResultCount(prepared.kept.length)
        setTotalAvailable(prepared.totalAvailable)
        setTruncated(prepared.truncated)
      } else {
        const data = await searchPropertiesInArea(geohashes, 'legacy')
        const parsed = (data.properties ?? []).map((hit) =>
          parseProperty(hit.id, (hit.data ?? {}) as Record<string, unknown>),
        )
        const prepared = prepareAreaSearchPins(
          parsed,
          viewport.bounds,
          viewport.center,
          MAP_PIN_HARD_BUDGET,
        )
        lastSearchKeyRef.current = key
        setProperties(prepared.kept)
        setResultCount(prepared.kept.length)
        setTotalAvailable(prepared.totalAvailable)
        setTruncated(prepared.truncated)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Map search failed')
    } finally {
      setLoading(false)
    }
  }, [loading, properties.length])

  const statusChip =
    resultCount != null && !loading && !groupSheet
      ? truncated && totalAvailable != null
        ? `Showing ${MAP_PIN_HARD_BUDGET.toLocaleString()} of ${totalAvailable.toLocaleString()} — zoom in for more`
        : `${resultCount.toLocaleString()} propert${resultCount === 1 ? 'y' : 'ies'}`
      : null

  return (
    <div className="biddle-map-page relative h-full min-h-0 w-full overflow-hidden bg-surface">
      {!appCheckReady ? (
        <div className="flex h-full items-center justify-center gap-2 text-sm text-muted">
          <Spinner />
          Preparing map…
        </div>
      ) : (
        <GoogleMap
          className="h-full w-full"
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={13}
          {...(MAP_ID ? { mapId: MAP_ID } : {})}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={false}
          clickableIcons={false}
        >
          <MapController
            active={active}
            onViewport={onViewport}
            flyTo={flyTo}
            onMapClick={clearSelection}
          />
          <MapFullscreenControl />
          <StreetViewVisibilityWatcher onVisibleChange={onStreetViewVisibleChange} />
          <MapTypeSync mapTypeId={mapTypeId} onMapTypeIdChange={onMapTypeIdChange} />
          <ClusterLayerController
            index={pinIndex}
            enabled={useClustering}
            onClusters={onClusters}
          />
          <ClusterExpandController target={expandTarget} />
          {MAP_ID && !useClustering
            ? groups.map((group) => (
                <PropertyGroupMarker
                  key={group.key}
                  group={group}
                  selected={
                    group.properties.length === 1 &&
                    group.properties[0].id === selectedId
                  }
                  onSelect={() => onGroupSelect(group)}
                />
              ))
            : null}
          {MAP_ID && useClustering
            ? visibleClusters.map((item) =>
                item.kind === 'cluster' ? (
                  <ClusterBadgeMarker
                    key={`c-${item.id}`}
                    item={item}
                    onExpand={() => {
                      if (!pinIndex) return
                      setExpandTarget({
                        clusterId: item.id,
                        lat: item.lat,
                        lng: item.lng,
                        index: pinIndex,
                      })
                    }}
                  />
                ) : (
                  <LeafPinMarker
                    key={`p-${coordKey(item.lat, item.lng)}`}
                    item={item}
                    selected={
                      item.count === 1 && item.propertyId === selectedId
                    }
                    onSelect={() => onLeafSelect(item)}
                  />
                ),
              )
            : null}
        </GoogleMap>
      )}

      {!streetViewOpen ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-3 md:p-4">
          <div className="pointer-events-auto mx-auto max-w-xl">
            {appCheckReady ? <MapLocationSearch onLocate={onLocate} /> : null}
            {error ? (
              <p className="mt-2 rounded-xl bg-white/95 px-3 py-2 text-sm text-danger shadow">
                {error}
              </p>
            ) : null}
          </div>
          {/* Map/Satellite stack — mobile clears the search bar; desktop sits just under Google’s fullscreen control (40px @ 10px inset + small gap). */}
          {appCheckReady ? (
            <div className="pointer-events-auto absolute right-3 top-[4.25rem] md:right-[10px] md:top-[58px]">
              <MapTypeToggleButtons mapTypeId={mapTypeId} onChange={onMapTypeIdChange} />
            </div>
          ) : null}
        </div>
      ) : null}

      {!streetViewOpen ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[22px] z-10 flex flex-col items-center gap-2.5 px-3 md:bottom-[30px]">
          {statusChip ? (
            <p className="rounded-full bg-white/95 px-3 py-1 text-xs font-medium text-muted shadow">
              {statusChip}
            </p>
          ) : null}
          {!searchZoomOk && !groupSheet ? (
            <p className="rounded-full bg-white/95 px-3 py-1 text-xs font-medium text-muted shadow">
              Zoom in to search this area
            </p>
          ) : null}
          {!groupSheet ? (
            <Button
              type="button"
              variant="outline"
              className="pointer-events-auto rounded-full bg-white px-5 shadow-lg disabled:opacity-60"
              disabled={!appCheckReady || loading || !searchZoomOk}
              onClick={() => void searchThisArea()}
            >
              {loading ? <Spinner className="h-4 w-4" /> : null}
              Search this area
            </Button>
          ) : null}
        </div>
      ) : null}

      {groupSheet ? (
        <PropertyGroupSheet
          properties={groupSheet.properties}
          onClose={() => setGroupSheetKey(null)}
          onSelect={(property) => {
            setGroupSheetKey(null)
            setSelectedId(property.id)
            openPropertyProfile(navigate, property.id)
          }}
        />
      ) : null}
    </div>
  )
}

export function MapPage({ active = true }: { active?: boolean }) {
  if (!MAPS_KEY) {
    return (
      <div className="flex h-full flex-col gap-4 p-4 md:p-8">
        <h1 className="text-2xl font-bold text-navy">Map</h1>
        <p className="text-sm text-muted">
          Set <code className="rounded bg-surface px-1">VITE_GOOGLE_MAPS_API_KEY</code> to enable
          the interactive map. Address search still works on the Search tab.
        </p>
        <PropertySearchBox />
      </div>
    )
  }

  return <MapCanvas active={active} />
}
